import { defineStore } from 'pinia';
import { computed, ref } from 'vue';
import type { UserRoleLegacy } from '@getaccept/lib-shared/src/users/types/user-role-legacy';
import type { LaunchDarklyFlags } from '@getaccept/lib-shared/src/launch-darkly/types/launch-darkly-flags';
import { useDebounceFn } from '@vueuse/core';
import { Context } from '../types';
import type { EBlockWidth } from '../types/enums';
import { EditorHelper } from '../helpers/editor.helper';
import { getIsAdmin } from '../helpers/context-menu/context-menu-root.helper';

export const useEditorMetaDataStore = defineStore('editor-meta-data', () => {
  const context = ref<Context>(null);
  const userId = ref('');
  const userRole = ref<UserRoleLegacy>(null);
  const isAddingRecipient = ref(false);
  const launchDarklyFlags = ref<LaunchDarklyFlags>({});
  const documentId = ref('');
  const resourceId = ref('');
  const dsrTemplateId = ref('');
  const dsrResourceId = ref('');
  const isBlockResizing = ref(false);
  const blockWidth = ref({
    xxs: false,
    xs: false,
    sm: false,
    md: false,
    lg: false,
    xl: false,
  });
  const blockWidthClass = ref<EBlockWidth>(null);
  const blockOffsetWidth = ref(0);

  const setBlockWidth = (width: number) => {
    if (EditorHelper.isSlideShowLightboxVisible()) {
      return;
    }
    blockWidth.value = EditorHelper.getBlockWidth(width);
    blockWidthClass.value = EditorHelper.getBlockWidthClass(blockWidth.value);
  };

  const updateBlockWidth = (width: number) => {
    blockOffsetWidth.value = width || 800;
    setBlockWidth(blockOffsetWidth.value);
    isBlockResizing.value = false;
  };

  const debounceUpdateBlockWidth = useDebounceFn((width: number) => updateBlockWidth(width), 200);

  const handleBlockResize = ({ offsetWidth }: HTMLDivElement) => {
    if (blockOffsetWidth.value === offsetWidth) {
      return;
    }
    isBlockResizing.value = true;
    debounceUpdateBlockWidth(offsetWidth);
  };

  const contractsTemplateId = computed(() =>
    context.value === Context.ContractsTemplate ? documentId.value : ''
  );

  const setContext = (newContext: Context) => {
    context.value = newContext;
  };

  const isDsr = computed(() => EditorHelper.isDealroomContext(context.value));

  const isDrSenderSite = computed(() =>
    [Context.DsrEdit, Context.DsrTemplate, Context.DsrResource].includes(context.value)
  );

  const setUserRole = (role: UserRoleLegacy) => {
    userRole.value = role;
  };

  const isAdmin = computed(() => getIsAdmin(userRole.value));

  const setUserId = (id: string) => {
    userId.value = id;
  };

  const setIsAddingRecipient = (value: boolean) => {
    isAddingRecipient.value = value;
  };

  const setLaunchDarklyFlags = (flags: LaunchDarklyFlags) => {
    launchDarklyFlags.value = flags;
  };

  const setResourceIds = (ids: {
    documentId: string;
    resourceId: string;
    dsrTemplateId: string;
    dsrResourceId: string;
  }) => {
    documentId.value = ids.documentId;
    resourceId.value = ids.resourceId;
    dsrTemplateId.value = ids.dsrTemplateId;
    dsrResourceId.value = ids.dsrResourceId;
  };

  return {
    setContext,
    setUserRole,
    setUserId,
    setIsAddingRecipient,
    setLaunchDarklyFlags,
    setResourceIds,
    updateBlockWidth,
    handleBlockResize,
    context,
    isDsr,
    isDrSenderSite,
    userRole,
    userId,
    isAdmin,
    isAddingRecipient,
    launchDarklyFlags,
    resourceId,
    contractsTemplateId,
    dsrTemplateId,
    dsrResourceId,
    isBlockResizing,
    blockWidthClass,
    blockWidth,
    blockOffsetWidth,
  };
});
